import type { FilterChip } from '@/components/filter-bar'
import type { FiltroListagemAlerta } from '@/types/dashboard'
import type { ListarGuiasFiltro } from '@/types/guia'
import { resolveSituacaoGuia, resolveTipoGuia } from '@/types/domain'
import { formatDateDefault, toApiDate } from '@/utility/Utils'

export interface FiltroGuias {
  nomePaciente: string
  numeroCarteira: string
  situacoes: string[]
  intervaloEmissao: Date[]
  procedimento: string
  observacao: string
  clienteId: string
  operadoraId: string
  credencialId: string
  numeroGuiaPrestador: string
  numeroGuiaOperadora: string
  senhaAutorizacao: string
  tipoGuia: string
  intervaloAutorizacao: Date[]
}

export const filtroVazio: FiltroGuias = {
  nomePaciente: '',
  numeroCarteira: '',
  situacoes: [],
  intervaloEmissao: [],
  procedimento: '',
  observacao: '',
  clienteId: '',
  operadoraId: '',
  credencialId: '',
  numeroGuiaPrestador: '',
  numeroGuiaOperadora: '',
  senhaAutorizacao: '',
  tipoGuia: '',
  intervaloAutorizacao: [],
}

const CAMPOS_PAINEL = [
  'numeroCarteira',
  'situacoes',
  'intervaloEmissao',
  'procedimento',
  'observacao',
  'clienteId',
  'operadoraId',
  'credencialId',
  'numeroGuiaPrestador',
  'numeroGuiaOperadora',
  'senhaAutorizacao',
  'tipoGuia',
  'intervaloAutorizacao',
] as const

const TODOS_OS_CAMPOS = ['nomePaciente', ...CAMPOS_PAINEL] as const

const preenchido = (valor: string | string[] | Date[]): boolean =>
  Array.isArray(valor) ? valor.length > 0 : valor.trim() !== ''

export const contarFiltrosDoPainel = (filtro: FiltroGuias): number =>
  CAMPOS_PAINEL.filter((campo) => preenchido(filtro[campo])).length

export const paraFiltroApi = (filtro: FiltroGuias, pageSize: number): ListarGuiasFiltro => ({
  pageNumber: 1,
  pageSize,
  nomePaciente: filtro.nomePaciente || null,
  numeroCarteira: filtro.numeroCarteira || null,
  situacoes: filtro.situacoes,
  dataInicial: toApiDate(filtro.intervaloEmissao[0]) ?? null,
  dataFinal: toApiDate(filtro.intervaloEmissao[1]) ?? null,
  procedimento: filtro.procedimento || null,
  observacao: filtro.observacao || null,
  clienteId: filtro.clienteId || null,
  operadoraId: filtro.operadoraId || null,
  credencialId: filtro.credencialId || null,
  numeroGuiaPrestador: filtro.numeroGuiaPrestador || null,
  numeroGuiaOperadora: filtro.numeroGuiaOperadora || null,
  senhaAutorizacao: filtro.senhaAutorizacao || null,
  tipoGuia: filtro.tipoGuia || null,
  dataAutorizacaoInicial: toApiDate(filtro.intervaloAutorizacao[0]) ?? null,
  dataAutorizacaoFinal: toApiDate(filtro.intervaloAutorizacao[1]) ?? null,
})

const ROTULOS: Record<keyof FiltroGuias, string> = {
  nomePaciente: 'Beneficiário',
  numeroCarteira: 'Carteira',
  situacoes: 'Situação',
  intervaloEmissao: 'Emissão',
  procedimento: 'Procedimento',
  observacao: 'Observação',
  clienteId: 'Cliente',
  operadoraId: 'Operadora',
  credencialId: 'Credencial',
  numeroGuiaPrestador: 'Nº guia prestador',
  numeroGuiaOperadora: 'Nº guia operadora',
  senhaAutorizacao: 'Senha da autorização',
  tipoGuia: 'Tipo de guia',
  intervaloAutorizacao: 'Autorização',
}

const textoDoValor = (campo: keyof FiltroGuias, filtro: FiltroGuias): string => {
  if (campo === 'intervaloEmissao') {
    const [inicio, fim] = filtro.intervaloEmissao
    return `${formatDateDefault(inicio)} a ${formatDateDefault(fim)}`
  }

  if (campo === 'intervaloAutorizacao') {
    const [inicio, fim] = filtro.intervaloAutorizacao
    return `${formatDateDefault(inicio)} a ${formatDateDefault(fim)}`
  }

  if (campo === 'situacoes') {
    return filtro.situacoes.length === 1
      ? resolveSituacaoGuia(filtro.situacoes[0]).label
      : `${filtro.situacoes.length} situações`
  }

  if (campo === 'tipoGuia') {
    return resolveTipoGuia(filtro.tipoGuia)
  }

  return filtro[campo]
}

export const montarChips = (
  filtro: FiltroGuias,
  remover: (campo: keyof FiltroGuias) => void,
): FilterChip[] =>
  (TODOS_OS_CAMPOS as readonly (keyof FiltroGuias)[])
    .filter((campo) => preenchido(filtro[campo]))
    .map((campo) => ({
      key: campo,
      label: ROTULOS[campo],
      value: textoDoValor(campo, filtro),
      onRemove: () => remover(campo),
    }))

export const filtroDeAlerta = (alerta: FiltroListagemAlerta): FiltroGuias => ({
  ...filtroVazio,
  situacoes: alerta.situacoes ?? [],
  clienteId: alerta.clienteId ?? '',
  operadoraId: alerta.operadoraId ?? '',
  credencialId: alerta.credencialId ?? '',
  intervaloEmissao:
    alerta.dataInicial && alerta.dataFinal
      ? [new Date(alerta.dataInicial), new Date(alerta.dataFinal)]
      : [],
})
